import React, { forwardRef, useMemo } from "react";
import { Box, BoxProps } from "@chakra-ui/react";
import { ff } from "../theme";
import DOMPurify from "dompurify";
import { Marked } from "marked";

/**
 * Renders brand-written Markdown (campaign brief, brand description) written in the dashboard's
 * rich text editor. Older values are plain text, which is valid Markdown; `breaks` keeps their
 * single newlines as line breaks the way `whiteSpace="pre-line"` used to.
 */

const markdown = new Marked({
  breaks: true,
  gfm: true,
  renderer: {
    link({ href, tokens }) {
      const safeHref = href.replace(/&/g, "&amp;").replace(/"/g, "&quot;");
      return `<a href="${safeHref}" target="_blank" rel="noopener noreferrer">${this.parser.parseInline(tokens)}</a>`;
    },
  },
});

const SANITIZE_OPTIONS = {
  ALLOWED_TAGS: ["h1", "h2", "h3", "p", "br", "strong", "em", "ul", "ol", "li", "a"],
  ALLOWED_ATTR: ["href", "target", "rel", "start"],
};

export const markdownToSafeHtml = (text: string): string =>
  DOMPurify.sanitize(markdown.parse(text, { async: false }), SANITIZE_OPTIONS);

interface MarkdownTextProps extends BoxProps {
  children: string | null | undefined;
}

const MarkdownText = forwardRef<HTMLDivElement, MarkdownTextProps>(({ children, sx, ...rest }, ref) => {
  const html = useMemo(() => markdownToSafeHtml(children ?? ""), [children]);
  return (
    <Box
      ref={ref}
      sx={{
        // `&`-prefixed: a bare `p` key in Chakra's sx is the padding shorthand, not the <p> tag.
        "& p, & h1, & h2, & h3": { margin: 0 },
        "& > * + *": { mt: 3 },
        "& h1, & h2, & h3": { color: ff.ink, fontFamily: "display", fontWeight: 600, letterSpacing: "-0.01em" },
        "& h1": { fontSize: "20px", lineHeight: 1.25 },
        "& h2": { fontSize: "17px", lineHeight: 1.3 },
        "& h3": { fontSize: "15px", lineHeight: 1.4 },
        "& strong": { color: ff.ink, fontWeight: 600 },
        "& ul, & ol": { pl: 5 },
        "& a": { color: ff.ink, fontWeight: 600, textDecoration: "underline" },
        ...sx,
      }}
      dangerouslySetInnerHTML={{ __html: html }}
      {...rest}
    />
  );
});

MarkdownText.displayName = "MarkdownText";

export default MarkdownText;
